import { create } from "zustand";
import { apiClient } from "@yrbp/api-client";

export interface UserInfo {
  id?: string;
  username: string;
  role: string;
  full_name?: string;
  mahalla_id?: string | null;
}

interface AuthState {
  access: string | null;
  refresh: string | null;
  user: UserInfo | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: () => boolean;
}

const LS_ACCESS  = "yrbp-access-token";
const LS_REFRESH = "yrbp-refresh-token";
const LS_USER    = "yrbp-user";

export const useAuth = create<AuthState>((set, get) => ({
  access:  localStorage.getItem(LS_ACCESS),
  refresh: localStorage.getItem(LS_REFRESH),
  user: (() => {
    try { return JSON.parse(localStorage.getItem(LS_USER) ?? "null"); }
    catch { return null; }
  })(),

  isAuthenticated: () => {
    // Always read from localStorage so we pick up tokens refreshed by apiClient
    const access = localStorage.getItem(LS_ACCESS);
    if (!access) return false;
    try {
      const { exp } = JSON.parse(atob(access.split(".")[1] ?? ""));
      if (exp * 1000 <= Date.now()) {
        // Access token expired — only logout if refresh token is also gone
        const refresh = localStorage.getItem(LS_REFRESH);
        if (!refresh) {
          localStorage.removeItem(LS_ACCESS);
          localStorage.removeItem(LS_REFRESH);
          localStorage.removeItem(LS_USER);
          set({ access: null, refresh: null, user: null });
          return false;
        }
        // Refresh token still valid — stay authenticated, apiClient will refresh on next 401
        return true;
      }
      // Sync zustand state if apiClient refreshed token behind our back
      if (access !== get().access) set({ access });
      return true;
    } catch {
      return false;
    }
  },

  login: async (username, password) => {
    const res = await apiClient.post("/api/v1/auth/login/", { username, password });
    const { access, refresh, user } = res.data;
    localStorage.setItem(LS_ACCESS,  access);
    localStorage.setItem(LS_REFRESH, refresh);
    localStorage.setItem(LS_USER,    JSON.stringify(user));
    set({ access, refresh, user });
  },

  logout: () => {
    localStorage.removeItem(LS_ACCESS);
    localStorage.removeItem(LS_REFRESH);
    localStorage.removeItem(LS_USER);
    set({ access: null, refresh: null, user: null });
  },
}));

// Sync store when apiClient silently refreshes the access token
if (typeof window !== "undefined") {
  window.addEventListener("yrbp:token-refreshed", ((e: CustomEvent<{ access: string }>) => {
    useAuth.setState({ access: e.detail.access });
  }) as EventListener);
}
